<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="welcome-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-dialog-title"
    >
      <div class="welcome-dialog__backdrop" />
      <div class="welcome-dialog__panel">
        <div v-if="step === 'character'" class="welcome-dialog__body">
          <BrandLogo size="sm" class="welcome-dialog__logo" />
          <p class="welcome-dialog__eyebrow">اولین ورود</p>
          <h2 id="welcome-dialog-title" class="welcome-dialog__title">
            {{ greeting }}
          </h2>
          <p class="welcome-dialog__lead">
            به کوکونی خوش آمدی. شخصیتت را انتخاب کن تا در پروفایل و جامعه یادگیری دیده شود.
            بعداً همین‌جا یک تور کوتاه از سایت اضافه می‌شود.
          </p>

          <div class="welcome-dialog__grid" role="listbox" aria-label="شخصیت‌ها">
            <button
              v-for="avatar in avatars"
              :key="avatar.id"
              type="button"
              class="welcome-dialog__option"
              :class="{ 'welcome-dialog__option--active': selectedId === avatar.id }"
              :aria-selected="selectedId === avatar.id"
              :title="avatar.label"
              :disabled="saving"
              @click="selectedId = avatar.id"
            >
              <img :src="avatar.src" :alt="avatar.label" />
            </button>
          </div>

          <p v-if="errorMessage" class="welcome-dialog__error">{{ errorMessage }}</p>

          <div class="welcome-dialog__actions">
            <button
              type="button"
              class="welcome-dialog__primary"
              :disabled="!selectedId || saving"
              @click="confirmCharacter"
            >
              {{ saving ? "در حال ذخیره..." : "ادامه" }}
            </button>
            <button
              type="button"
              class="welcome-dialog__ghost"
              :disabled="saving"
              @click="skipForNow"
            >
              بعداً انتخاب می‌کنم
            </button>
          </div>
        </div>

        <div v-else class="welcome-dialog__body welcome-dialog__body--tour">
          <!-- Product tour / tutorial slides go here. -->
          <h2 id="welcome-dialog-title" class="welcome-dialog__title">آشنایی با کوکونی</h2>
          <p class="welcome-dialog__lead">
            تور آموزشی سایت به‌زودی در این مرحله نمایش داده می‌شود.
          </p>
          <button type="button" class="welcome-dialog__primary" @click="finish">
            شروع کنیم
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { updateMe } from "@/api/users";
import { useAuth } from "@/composables/useAuth";
import BrandLogo from "@/components/BrandLogo.vue";
import { COCO_AVATARS } from "@/constants/avatars";
import { handleError } from "@/services/errorHandler";

/** Flip to true when welcome-dialog tour slides are implemented. */
const TOUR_ENABLED = false;

const route = useRoute();
const { user, fetchCurrentUser } = useAuth();
const avatars = COCO_AVATARS;

const dismissed = ref(false);
const selectedId = ref<string | null>(null);
const saving = ref(false);
const errorMessage = ref("");
const step = ref<"character" | "tour">("character");

const greeting = computed(() => {
  const name = user.value?.full_name?.trim();
  if (name) return `${name}، خوش آمدی`;
  const handle = typeof user.value?.username === "string" ? user.value.username : "";
  if (handle) return `@${handle}، خوش آمدی`;
  return "خوش آمدی";
});

const open = computed(() => {
  if (dismissed.value) return false;
  if (route.meta.public === true) return false;
  if (!user.value?.id) return false;
  if (user.value.avatar_url) return false;
  return true;
});

watch(open, (isOpen) => {
  document.body.style.overflow = isOpen ? "hidden" : "";
  if (isOpen) {
    step.value = "character";
    selectedId.value = null;
    errorMessage.value = "";
  }
});

async function confirmCharacter() {
  if (!selectedId.value || saving.value) return;
  saving.value = true;
  errorMessage.value = "";
  try {
    await updateMe({ avatar_url: selectedId.value });
    await fetchCurrentUser();
    if (TOUR_ENABLED) {
      step.value = "tour";
    } else {
      finish();
    }
  } catch (e) {
    errorMessage.value = handleError(e, "WelcomeOnboardingDialog.confirmCharacter");
  } finally {
    saving.value = false;
  }
}

function skipForNow() {
  dismissed.value = true;
}

function finish() {
  dismissed.value = true;
}
</script>

<style scoped>
.welcome-dialog {
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  direction: rtl;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.welcome-dialog__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(44, 32, 24, 0.45);
  backdrop-filter: blur(4px);
}

.welcome-dialog__panel {
  position: relative;
  z-index: 1;
  width: min(560px, 100%);
  max-height: min(92vh, 760px);
  overflow: auto;
  background: #fff;
  border: 1px solid #ece4d9;
  border-radius: 24px;
  box-shadow: 0 18px 48px rgba(76, 55, 42, 0.18);
}

.welcome-dialog__body {
  padding: 28px 28px 24px;
}

.welcome-dialog__logo {
  margin: 0 auto 8px;
}

.welcome-dialog__eyebrow {
  margin: 0 0 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6d5ef8;
}

.welcome-dialog__title {
  margin: 0 0 10px;
  text-align: center;
  font-size: 24px;
  font-weight: 800;
  color: #4c372a;
}

.welcome-dialog__lead {
  margin: 0 0 20px;
  text-align: center;
  font-size: 14px;
  line-height: 1.8;
  color: #8c8176;
}

.welcome-dialog__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.welcome-dialog__option {
  aspect-ratio: 1;
  border: 2px solid #ece4d9;
  border-radius: 16px;
  background: #fffdf8;
  padding: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.welcome-dialog__option img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.welcome-dialog__option:hover:not(:disabled) {
  border-color: #c4b5fd;
  transform: translateY(-1px);
}

.welcome-dialog__option--active {
  border-color: #6d5ef8;
  box-shadow: 0 0 0 3px rgba(109, 94, 248, 0.2);
}

.welcome-dialog__error {
  margin: 0 0 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #c45c5c;
}

.welcome-dialog__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.welcome-dialog__primary,
.welcome-dialog__ghost {
  width: 100%;
  border: none;
  border-radius: 14px;
  padding: 12px 16px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.welcome-dialog__primary {
  background: #6d5ef8;
  color: #fff;
}

.welcome-dialog__primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.welcome-dialog__ghost {
  background: transparent;
  color: #8c8176;
}

@media (max-width: 576px) {
  .welcome-dialog__body {
    padding: 22px 18px 18px;
  }

  .welcome-dialog__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
